/**
 * De app-shell: navigatie links, scherm rechts.
 *
 * De routes volgen de rechten van de rol. Een productiemedewerker die `/dashboard`
 * intypt komt op de werkvloer uit in plaats van op een leeg scherm — en zou hij het
 * toch forceren, dan geeft de API een 403.
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import type { Recht } from './api';
import { useGegevens } from './gegevens';
import { IconHamburger, IconKruis } from './onderdelen/Iconen';
import { NotificatiesPaneel, useNotificaties } from './onderdelen/Notificaties';
import { APPNAAM, bewaarZijbalk, Zijbalk, zijbalkIngeklapt } from './onderdelen/Zijbalk';
import { leesStartscherm } from './schermen/Instellingen';
import { Aanmelden } from './schermen/Aanmelden';
import { Beheer } from './schermen/Beheer';
import { Bewerkingen } from './schermen/Bewerkingen';
import { Dashboard } from './schermen/Dashboard';
import { Houtstaat } from './schermen/Houtstaat';
import { Planning } from './schermen/Planning';
import { Werkvloer } from './schermen/Werkvloer';
import { Tomtomscherm } from './schermen/Tomtomscherm';

/**
 * Het 3D-scherm brengt three.js mee en dat is het grootste deel van de bundel. Het
 * wordt pas geladen als iemand het model opent, zodat de werkvloer en de houtstaat —
 * die het niet nodig hebben — snel blijven, ook op een tablet in de werkplaats.
 */
const Model = lazy(() => import('./schermen/Model').then((m) => ({ default: m.Model })));

/**
 * `/login` (of `#/login`) toont altijd het aanmeldscherm, ook als je al bent aangemeld:
 * zo is het altijd naar voren te halen. SWA levert op dat pad gewoon `index.html`.
 */
const opLoginpad = (): boolean =>
  window.location.pathname.replace(/\/+$/, '') === '/login' ||
  window.location.hash.replace(/^#/, '').replace(/\/+$/, '') === '/login';

/** Het startscherm uit Instellingen, of anders het eerste scherm dat deze rol mag zien. */
function startpad(mag: (recht: Recht) => boolean): string {
  const gekozen = leesStartscherm();
  if (gekozen && mag(gekozen.recht)) return gekozen.pad;
  if (mag('houtstaatBekijken')) return '/houtstaat';
  if (mag('werkvloerBekijken')) return '/werkvloer';
  if (mag('dashboardBekijken')) return '/dashboard';
  return '/houtstaat';
}

export function App() {
  const { ik, profiel, mag, boot, laden, nietIngelogd, fout, meldFout } = useGegevens();
  const [ingeklapt, setIngeklapt] = useState(zijbalkIngeklapt);
  const [lade, setLade] = useState(false);
  const navigeer = useNavigate();
  const { pathname } = useLocation();

  const zetIngeklapt = useCallback((waarde: boolean) => {
    setIngeklapt(waarde);
    bewaarZijbalk(waarde);
  }, []);

  // Een link buiten het menu sluit de schuiflade ook.
  useEffect(() => setLade(false), [pathname]);

  const { regels, gelezen, ongelezen, markeerGelezen } = useNotificaties();
  const [meldknop, setMeldknop] = useState<HTMLElement | null>(null);
  const [meldingenOpen, setMeldingenOpen] = useState(false);
  const sluitMeldingen = useCallback(() => {
    setMeldingenOpen(false);
    markeerGelezen();
  }, [markeerGelezen]);
  const notificaties = {
    open: meldingenOpen,
    ongelezen,
    onKlik: (knop: HTMLButtonElement) => {
      setMeldknop(knop);
      if (meldingenOpen) sluitMeldingen();
      else setMeldingenOpen(true);
    },
  };

  // De tomtom op een tweede monitor draait zonder shell: dat venster hoort alleen de
  // route te tonen. Het pad wordt door de werkvloer geopend met window.open.
  const tweedeScherm = window.location.hash.startsWith('#/tomtom');

  const start = useMemo(() => startpad(mag), [mag]);

  useEffect(() => {
    if (!ik) return;
    // Rechtstreeks naar een scherm dat deze rol niet mag: stuur hem naar zijn eigen
    // startscherm in plaats van hem naar een 403 te laten lopen.
    const pad = window.location.hash.replace(/^#/, '');
    const grens: Array<[string, Recht]> = [
      ['/houtstaat', 'houtstaatBekijken'],
      ['/werkvloer', 'werkvloerBekijken'],
      ['/dashboard', 'dashboardBekijken'],
      ['/planning', 'planningBekijken'],
      ['/model', 'modelBekijken'],
      ['/bewerkingen', 'regelsBekijken'],

    ];
    for (const [begin, recht] of grens) {
      if (pad.startsWith(begin) && !mag(recht)) {
        navigeer(start, { replace: true });
        return;
      }
    }
  }, [ik, mag, navigeer, start]);

  if (opLoginpad()) return <Aanmelden aangemeldAls={ik ? (profiel?.weergavenaam ?? ik.naam) : null} />;
  if (nietIngelogd) return <Aanmelden />;
  if (tweedeScherm) {
    return (
      <Routes>
        <Route path="/tomtom/:karId" element={<Tomtomscherm />} />
      </Routes>
    );
  }
  if (laden && !boot) {
    return <div className="hs-leeg">Houtstaat wordt geladen…</div>;
  }

  return (
    <div
      className="hs-app"
      style={{ '--zijbalk-huidig': ingeklapt ? 'var(--zijbalk-ingeklapt)' : 'var(--zijbalk-breed)' } as CSSProperties}
    >
      {/* Een knop en geen #-link: de HashRouter zou die als route lezen. */}
      <button type="button" className="hs-naar-inhoud" onClick={() => document.getElementById('inhoud')?.focus()}>
        Naar de inhoud
      </button>

      {/* Vanaf 1024 px een vaste zijbalk die niet meescrolt. */}
      <aside className="hs-app__zijbalk">
        <Zijbalk ingeklapt={ingeklapt} zetIngeklapt={zetIngeklapt} notificaties={notificaties} />
      </aside>

      {/* Smaller: een bovenbalk met menuknop en een schuiflade. */}
      <div className="hs-bovenbalk">
        <span className="hs-bovenbalk__naam">{APPNAAM}</span>
        <button
          type="button"
          className="hs-icoonknop hs-icoonknop--donker"
          onClick={() => setLade(true)}
          aria-label="Menu openen"
          aria-expanded={lade}
        >
          <IconHamburger maat={24} />
        </button>
      </div>
      {lade ? (
        <div className="hs-lade" role="dialog" aria-modal="true" aria-label="Menu">
          <button type="button" className="hs-lade__sluier" aria-label="Menu sluiten" onClick={() => setLade(false)} />
          <div className="hs-lade__paneel">
            <Zijbalk onNavigeer={() => setLade(false)} notificaties={notificaties} />
            <button
              type="button"
              className="hs-icoonknop hs-icoonknop--donker hs-lade__sluit"
              onClick={() => setLade(false)}
              aria-label="Menu sluiten"
            >
              <IconKruis maat={24} />
            </button>
          </div>
        </div>
      ) : null}

      <NotificatiesPaneel
        knop={meldknop}
        open={meldingenOpen}
        onSluit={sluitMeldingen}
        regels={regels}
        gelezen={gelezen}
      />

      <main className="hs-inhoud" id="inhoud" tabIndex={-1}>
        {fout ? (
          <div className="hs-melding" style={{ marginBottom: 16 }} role="alert">
            <span style={{ flex: 1 }}>{fout}</span>
            <button type="button" className="hs-knop" onClick={() => meldFout(null)}>
              Sluiten
            </button>
          </div>
        ) : null}

        <Routes>
          <Route path="/" element={<Navigate to={start} replace />} />
          <Route path="/houtstaat" element={<Houtstaat />} />
          {/* Met een karnummer erachter is een kar aan te linken: handig om iemand
              rechtstreeks naar de juiste kar te sturen. */}
          <Route path="/houtstaat/:karId" element={<Houtstaat />} />
          <Route path="/werkvloer" element={<Werkvloer />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/planning" element={<Planning />} />
          <Route path="/bewerkingen" element={<Bewerkingen />} />
          <Route
            path="/model/:bouwelementId"
            element={
              <Suspense fallback={<div className="hs-leeg">De 3D-viewer wordt geladen…</div>}>
                <Model />
              </Suspense>
            }
          />
          <Route path="/instellingen" element={<Beheer />} />
          {/* Beheer staat sinds de Gate Review-indeling onder Instellingen. */}
          <Route path="/beheer" element={<Navigate to="/instellingen" replace />} />
          <Route path="*" element={<Navigate to={start} replace />} />
        </Routes>
      </main>

    </div>
  );
}
